import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildProgressSegmentPath,
  buildSmoothClosedPath,
  buildTrackMetrics,
  getNearestProgressToPoint,
  getWrappedMidpoint,
  normalizeProgress,
  sampleTrackPosition,
} from '../utils/slotCars';

const DEFAULT_CORNER_SPAN = 0.05;

function getApiBaseUrl() {
  const explicitApiUrl = process.env.REACT_APP_API_URL;
  if (explicitApiUrl) {
    return explicitApiUrl.replace(/\/$/, '');
  }

  const explicitSocketUrl = process.env.REACT_APP_SOCKET_URL;
  if (explicitSocketUrl && explicitSocketUrl !== '/') {
    return explicitSocketUrl.replace(/\/$/, '');
  }

  const { protocol, hostname, port, origin } = window.location;
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && (port === '3000' || port === '')) {
    return `${protocol}//${hostname}:3001`;
  }

  return origin;
}

const DEFAULT_TRACK = {
  id: 'new-track',
  name: 'New Track',
  description: 'A compact custom track.',
  width: 280,
  height: 500,
  laneWidth: 28,
  lapsToWin: 3,
  maxSpeed: 0.2,
  acceleration: 0.16,
  deceleration: 0.1,
  crashRecoveryMs: 2600,
  startProgress: 0.02,
  startSpacing: 0.016,
  path: [
    { x: 140, y: 460 },
    { x: 88, y: 420 },
    { x: 76, y: 110 },
    { x: 186, y: 82 },
    { x: 206, y: 416 },
  ],
  corners: [],
  palette: {
    background: '#08131c',
    infield: '#143a2d',
    road: '#495461',
    roadEdge: '#ced6de',
    lane: '#f8fafc',
    accent: '#f97316',
  },
};

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function sanitizeTrack(track) {
  const nextTrack = deepClone(track || DEFAULT_TRACK);
  nextTrack.path = Array.isArray(nextTrack.path) ? nextTrack.path : [];
  nextTrack.corners = Array.isArray(nextTrack.corners) ? nextTrack.corners : [];
  nextTrack.palette = { ...DEFAULT_TRACK.palette, ...(nextTrack.palette || {}) };
  return nextTrack;
}

function clampNumber(value, fallback) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-sm text-slate-700 dark:text-slate-300">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{label}</span>
      {children}
    </label>
  );
}

export default function SlotCarsTrackEditor() {
  const [tracks, setTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [draftTrack, setDraftTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [draggingPointIndex, setDraggingPointIndex] = useState(null);
  const [clickMode, setClickMode] = useState('path');
  const [selectedCornerId, setSelectedCornerId] = useState(null);
  const svgRef = useRef(null);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const loadTracks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/slotcars/tracks`);
      const payload = await response.json();
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || 'Failed to load track data');
      }
      const nextTracks = Array.isArray(payload.tracks) ? payload.tracks.map(sanitizeTrack) : [];
      setTracks(nextTracks);
      setSelectedTrackId((current) => current || nextTracks[0]?.id || null);
      setError('');
    } catch (nextError) {
      setError(String(nextError.message || nextError));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  useEffect(() => {
    if (!selectedTrackId) {
      setDraftTrack(null);
      return;
    }
    const selected = tracks.find((track) => track.id === selectedTrackId);
    setDraftTrack(selected ? deepClone(selected) : null);
  }, [selectedTrackId, tracks]);

  useEffect(() => {
    const corners = draftTrack?.corners || [];
    if (!corners.length) {
      setSelectedCornerId(null);
      return;
    }
    if (!selectedCornerId || !corners.some((corner) => corner.id === selectedCornerId)) {
      setSelectedCornerId(corners[0].id);
    }
  }, [draftTrack?.corners, selectedCornerId]);

  const metrics = useMemo(() => buildTrackMetrics(draftTrack?.path || []), [draftTrack?.path]);
  const roadPath = useMemo(() => buildSmoothClosedPath(metrics.points || []), [metrics.points]);
  const renderedRoadWidth = Math.max(14, (draftTrack?.laneWidth || 40) - 4);
  const renderedRoadEdgeWidth = renderedRoadWidth + 8;
  const cornerMarkers = useMemo(() => {
    const corners = draftTrack?.corners || [];
    return corners.map((corner) => ({
      ...corner,
      zonePath: buildProgressSegmentPath(metrics, corner.start, corner.end, 22),
      point: sampleTrackPosition(metrics, getWrappedMidpoint(corner.start, corner.end), 0),
      startPoint: sampleTrackPosition(metrics, corner.start, 0),
      endPoint: sampleTrackPosition(metrics, corner.end, 0),
    }));
  }, [draftTrack?.corners, metrics]);

  const selectedCorner = useMemo(() => {
    if (!selectedCornerId || !draftTrack?.corners) return null;
    return draftTrack.corners.find((corner) => corner.id === selectedCornerId) || null;
  }, [draftTrack?.corners, selectedCornerId]);

  const updateDraft = useCallback((updater) => {
    setDraftTrack((current) => (current ? updater(deepClone(current)) : current));
  }, []);

  const saveTracks = useCallback(async () => {
    if (!draftTrack) return;
    setSaving(true);
    try {
      const nextTracks = tracks.map((track) => (track.id === selectedTrackId ? sanitizeTrack(draftTrack) : track));
      const response = await fetch(`${apiBaseUrl}/api/slotcars/tracks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracks: nextTracks }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || 'Failed to save track data');
      }
      const savedTracks = Array.isArray(payload.tracks) ? payload.tracks.map(sanitizeTrack) : nextTracks;
      setTracks(savedTracks);
      setSelectedTrackId(draftTrack.id);
      setStatus('Saved to server/gamecontent/slotcars/tracks.json');
      setError('');
    } catch (nextError) {
      setError(String(nextError.message || nextError));
    } finally {
      setSaving(false);
    }
  }, [apiBaseUrl, draftTrack, selectedTrackId, tracks]);

  const createTrack = useCallback(() => {
    const existingIds = new Set(tracks.map((track) => track.id));
    let counter = 1;
    let nextId = `custom-track-${counter}`;
    while (existingIds.has(nextId)) {
      counter += 1;
      nextId = `custom-track-${counter}`;
    }
    const newTrack = { ...deepClone(DEFAULT_TRACK), id: nextId, name: `Custom Track ${counter}` };
    const nextTracks = [...tracks, newTrack];
    setTracks(nextTracks);
    setSelectedTrackId(nextId);
  }, [tracks]);

  const removeSelectedTrack = useCallback(() => {
    if (!draftTrack || tracks.length <= 1) return;
    const nextTracks = tracks.filter((track) => track.id !== draftTrack.id);
    setTracks(nextTracks);
    setSelectedTrackId(nextTracks[0]?.id || null);
  }, [draftTrack, tracks]);

  const exportJson = useMemo(() => JSON.stringify(tracks.map((track) => track.id === selectedTrackId ? sanitizeTrack(draftTrack) : track), null, 2), [draftTrack, selectedTrackId, tracks]);

  const getSvgPointFromClient = useCallback((clientX, clientY) => {
    if (!svgRef.current || !draftTrack) return null;
    const bounds = svgRef.current.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return null;
    return {
      x: ((clientX - bounds.left) / bounds.width) * draftTrack.width,
      y: ((clientY - bounds.top) / bounds.height) * draftTrack.height,
    };
  }, [draftTrack]);

  const updatePointFromSvg = useCallback((clientX, clientY, pointIndex = null) => {
    const svgPoint = getSvgPointFromClient(clientX, clientY);
    if (!svgPoint) return;
    const nextX = svgPoint.x;
    const nextY = svgPoint.y;

    updateDraft((track) => {
      if (pointIndex === null) {
        track.path.push({ x: Math.round(nextX), y: Math.round(nextY) });
      } else if (track.path[pointIndex]) {
        track.path[pointIndex].x = Math.round(nextX);
        track.path[pointIndex].y = Math.round(nextY);
      }
      return track;
    });
  }, [getSvgPointFromClient, updateDraft]);

  const applyCornerClick = useCallback((clientX, clientY) => {
    const svgPoint = getSvgPointFromClient(clientX, clientY);
    if (!svgPoint) return;
    const progress = getNearestProgressToPoint(metrics, svgPoint);

    if (clickMode === 'corner-center') {
      updateDraft((track) => {
        const nextIndex = track.corners.length + 1;
        const halfSpan = DEFAULT_CORNER_SPAN / 2;
        const nextCorner = {
          id: `corner-${nextIndex}`,
          label: `Corner ${nextIndex}`,
          start: normalizeProgress(progress - halfSpan),
          end: normalizeProgress(progress + halfSpan),
          maxSpeed: 0.15,
          crashWindowRatio: 0.3,
          apexStrength: 0.22,
        };
        track.corners.push(nextCorner);
        setSelectedCornerId(nextCorner.id);
        return track;
      });
      return;
    }

    if (!selectedCornerId) return;

    updateDraft((track) => {
      const corner = track.corners.find((item) => item.id === selectedCornerId);
      if (!corner) return track;
      if (clickMode === 'corner-start') {
        corner.start = progress;
      }
      if (clickMode === 'corner-end') {
        corner.end = progress;
      }
      return track;
    });
  }, [clickMode, getSvgPointFromClient, metrics, selectedCornerId, updateDraft]);

  const onSvgPointerDown = useCallback((event, pointIndex = null) => {
    event.preventDefault();
    if (pointIndex !== null) {
      setDraggingPointIndex(pointIndex);
      event.stopPropagation();
    } else if (clickMode === 'path') {
      updatePointFromSvg(event.clientX, event.clientY, null);
    } else {
      applyCornerClick(event.clientX, event.clientY);
    }
  }, [applyCornerClick, clickMode, updatePointFromSvg]);

  useEffect(() => {
    if (draggingPointIndex === null) return undefined;
    const move = (event) => updatePointFromSvg(event.clientX, event.clientY, draggingPointIndex);
    const up = () => setDraggingPointIndex(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [draggingPointIndex, updatePointFromSvg]);

  return (
    <div className="mx-auto max-w-7xl p-4 text-slate-900 dark:text-slate-100">
      <div className="mb-6 rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight">SlotCars Track Editor</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Drag path points, tune orange corner bands, and save directly back to the SlotCars JSON file. Use the click mode to add points or place orange zones directly on the track.
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={createTrack} className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold shadow-sm dark:border-slate-700 dark:bg-slate-900">New Track</button>
            <button type="button" onClick={saveTracks} disabled={!draftTrack || saving} className="rounded-full border border-emerald-500 bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50">{saving ? 'Saving...' : 'Save JSON'}</button>
          </div>
        </div>
        {status ? <div className="mt-3 text-sm text-emerald-600 dark:text-emerald-300">{status}</div> : null}
        {error ? <div className="mt-3 text-sm text-rose-600 dark:text-rose-300">{error}</div> : null}
      </div>

      <div className="grid gap-6 xl:grid-cols-[280px_minmax(0,1fr)_360px]">
        <aside className="rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Tracks</div>
            <span className="text-xs text-slate-500 dark:text-slate-400">{tracks.length}</span>
          </div>
          <div className="space-y-2">
            {loading ? <div className="text-sm text-slate-500 dark:text-slate-400">Loading tracks...</div> : null}
            {tracks.map((track) => (
              <button
                key={track.id}
                type="button"
                onClick={() => setSelectedTrackId(track.id)}
                className={`w-full rounded-2xl border p-3 text-left transition ${track.id === selectedTrackId ? 'border-emerald-500 bg-emerald-50 dark:border-emerald-400 dark:bg-emerald-950/30' : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900/70'}`}
              >
                <div className="font-semibold">{track.name}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{track.id}</div>
              </button>
            ))}
          </div>
          <button type="button" onClick={removeSelectedTrack} disabled={!draftTrack || tracks.length <= 1} className="mt-4 w-full rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-600 disabled:opacity-40 dark:border-rose-800 dark:text-rose-300">
            Remove Selected Track
          </button>
        </aside>

        <section className="space-y-4 rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
          {draftTrack ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="Track ID"><input value={draftTrack.id} onChange={(event) => updateDraft((track) => ({ ...track, id: event.target.value }))} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                <Field label="Name"><input value={draftTrack.name} onChange={(event) => updateDraft((track) => ({ ...track, name: event.target.value }))} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                <Field label="Width"><input type="number" value={draftTrack.width} onChange={(event) => updateDraft((track) => ({ ...track, width: clampNumber(event.target.value, track.width) }))} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                <Field label="Height"><input type="number" value={draftTrack.height} onChange={(event) => updateDraft((track) => ({ ...track, height: clampNumber(event.target.value, track.height) }))} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                <Field label="Lane Width"><input type="number" value={draftTrack.laneWidth} onChange={(event) => updateDraft((track) => ({ ...track, laneWidth: clampNumber(event.target.value, track.laneWidth) }))} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                <Field label="Max Speed"><input type="number" step="0.01" value={draftTrack.maxSpeed} onChange={(event) => updateDraft((track) => ({ ...track, maxSpeed: clampNumber(event.target.value, track.maxSpeed) }))} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                <Field label="Acceleration"><input type="number" step="0.01" value={draftTrack.acceleration} onChange={(event) => updateDraft((track) => ({ ...track, acceleration: clampNumber(event.target.value, track.acceleration) }))} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                <Field label="Deceleration"><input type="number" step="0.01" value={draftTrack.deceleration} onChange={(event) => updateDraft((track) => ({ ...track, deceleration: clampNumber(event.target.value, track.deceleration) }))} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
              </div>

              <Field label="Description">
                <textarea value={draftTrack.description} onChange={(event) => updateDraft((track) => ({ ...track, description: event.target.value }))} rows={2} className="rounded-2xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" />
              </Field>

              <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/60">
                {[
                  { id: 'path', label: 'Click Adds Point' },
                  { id: 'corner-center', label: 'Click Adds Orange Zone' },
                  { id: 'corner-start', label: 'Click Sets Zone Start' },
                  { id: 'corner-end', label: 'Click Sets Zone End' },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setClickMode(mode.id)}
                    className={`rounded-full border px-3 py-2 text-sm font-semibold transition ${clickMode === mode.id ? 'border-amber-500 bg-amber-500 text-slate-950' : 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'}`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {clickMode === 'path' ? 'Canvas clicks add path points.' : null}
                {clickMode === 'corner-center' ? 'Canvas clicks create a new orange zone centered on the nearest point of the track.' : null}
                {clickMode === 'corner-start' ? `Canvas clicks move the start of ${selectedCorner?.label || 'the selected zone'}.` : null}
                {clickMode === 'corner-end' ? `Canvas clicks move the end of ${selectedCorner?.label || 'the selected zone'}.` : null}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-950/90 p-3 dark:border-slate-800">
                <svg
                  ref={svgRef}
                  viewBox={`0 0 ${draftTrack.width} ${draftTrack.height}`}
                  className="block h-auto w-full touch-none rounded-2xl"
                  onPointerDown={(event) => onSvgPointerDown(event, null)}
                >
                  <rect x="0" y="0" width={draftTrack.width} height={draftTrack.height} fill={draftTrack.palette.infield} />
                  <path d={roadPath} fill="none" stroke={draftTrack.palette.roadEdge} strokeWidth={renderedRoadEdgeWidth} strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
                  <path d={roadPath} fill="none" stroke={draftTrack.palette.road} strokeWidth={renderedRoadWidth} strokeLinecap="round" strokeLinejoin="round" />
                  {cornerMarkers.map((corner) => (
                    <g key={corner.id}>
                      <path d={corner.zonePath} fill="none" stroke={draftTrack.palette.accent} strokeWidth={renderedRoadWidth + 6} strokeLinecap="round" strokeLinejoin="round" opacity="0.2" />
                      <path d={corner.zonePath} fill="none" stroke={draftTrack.palette.accent} strokeWidth={corner.id === selectedCornerId ? '6' : '4'} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="12 10" opacity="0.95" />
                      <g transform={`translate(${corner.point.x} ${corner.point.y})`}>
                        <circle r="10" fill={corner.id === selectedCornerId ? '#fb923c' : draftTrack.palette.accent} stroke="#fff7ed" strokeWidth="2" onPointerDown={(event) => { event.preventDefault(); event.stopPropagation(); setSelectedCornerId(corner.id); }} />
                        <text x="0" y="3" textAnchor="middle" fontSize="8" fontWeight="700" fill="#fff7ed">{cornerMarkers.findIndex((item) => item.id === corner.id) + 1}</text>
                      </g>
                      {corner.id === selectedCornerId ? (
                        <>
                          <circle cx={corner.startPoint.x} cy={corner.startPoint.y} r="6" fill="#f8fafc" stroke="#ea580c" strokeWidth="2" />
                          <circle cx={corner.endPoint.x} cy={corner.endPoint.y} r="6" fill="#fed7aa" stroke="#ea580c" strokeWidth="2" />
                        </>
                      ) : null}
                    </g>
                  ))}
                  {draftTrack.path.map((point, index) => (
                    <g key={`${point.x}-${point.y}-${index}`} transform={`translate(${point.x} ${point.y})`}>
                      <circle r="9" fill="#0f172a" stroke="#f8fafc" strokeWidth="2" onPointerDown={(event) => onSvgPointerDown(event, index)} />
                      <text x="0" y="3" textAnchor="middle" fontSize="10" fill="#f8fafc">{index + 1}</text>
                    </g>
                  ))}
                </svg>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">Drag numbered points to reshape the track. Click the orange badge to select a zone, then use the click mode to place a new zone or move its start/end directly on the circuit.</div>
            </>
          ) : null}
        </section>

        <section className="space-y-4 rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70">
          {draftTrack ? (
            <>
              <div>
                <div className="mb-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Path Points</div>
                <div className="space-y-2">
                  {draftTrack.path.map((point, index) => (
                    <div key={`point-${index}`} className="grid grid-cols-[auto_1fr_1fr_auto] items-center gap-2">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">{index + 1}</div>
                      <input type="number" value={point.x} onChange={(event) => updateDraft((track) => { track.path[index].x = clampNumber(event.target.value, point.x); return track; })} className="rounded-xl border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900" />
                      <input type="number" value={point.y} onChange={(event) => updateDraft((track) => { track.path[index].y = clampNumber(event.target.value, point.y); return track; })} className="rounded-xl border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900" />
                      <button type="button" onClick={() => updateDraft((track) => { track.path.splice(index, 1); return track; })} className="rounded-full border border-rose-300 px-2 py-1 text-xs font-semibold text-rose-600 dark:border-rose-800 dark:text-rose-300">Del</button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Corner Bands</div>
                  <button type="button" onClick={() => {
                    updateDraft((track) => {
                      const nextCorner = { id: `corner-${track.corners.length + 1}`, label: `Corner ${track.corners.length + 1}`, start: 0, end: DEFAULT_CORNER_SPAN, maxSpeed: 0.15, crashWindowRatio: 0.3, apexStrength: 0.22 };
                      track.corners.push(nextCorner);
                      return track;
                    });
                    setClickMode('corner-start');
                  }} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold dark:border-slate-700">Add Corner</button>
                </div>
                <div className="space-y-4">
                  {draftTrack.corners.map((corner, index) => (
                    <div key={corner.id || index} className={`rounded-2xl border p-3 dark:border-slate-800 ${corner.id === selectedCornerId ? 'border-amber-400 bg-amber-50/70 dark:bg-amber-950/20' : 'border-slate-200'}`}>
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <button type="button" onClick={() => setSelectedCornerId(corner.id)} className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                          {corner.id}
                        </button>
                        {corner.id === selectedCornerId ? <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-950">Selected</span> : null}
                      </div>
                      <Field label="Label"><input value={corner.label} onChange={(event) => updateDraft((track) => { track.corners[index].label = event.target.value; return track; })} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <Field label="Start"><input type="number" step="0.001" value={corner.start} onChange={(event) => updateDraft((track) => { track.corners[index].start = clampNumber(event.target.value, corner.start); return track; })} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                        <Field label="End"><input type="number" step="0.001" value={corner.end} onChange={(event) => updateDraft((track) => { track.corners[index].end = clampNumber(event.target.value, corner.end); return track; })} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                        <Field label="Max Speed"><input type="number" step="0.001" value={corner.maxSpeed} onChange={(event) => updateDraft((track) => { track.corners[index].maxSpeed = clampNumber(event.target.value, corner.maxSpeed); return track; })} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                        <Field label="Crash Window"><input type="number" step="0.01" value={corner.crashWindowRatio} onChange={(event) => updateDraft((track) => { track.corners[index].crashWindowRatio = clampNumber(event.target.value, corner.crashWindowRatio); return track; })} className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-700 dark:bg-slate-900" /></Field>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 justify-end">
                        <button type="button" onClick={() => { setSelectedCornerId(corner.id); setClickMode('corner-start'); }} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold dark:border-slate-700">Pick Start</button>
                        <button type="button" onClick={() => { setSelectedCornerId(corner.id); setClickMode('corner-end'); }} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold dark:border-slate-700">Pick End</button>
                        <button type="button" onClick={() => updateDraft((track) => { track.corners.splice(index, 1); return track; })} className="rounded-full border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-600 dark:border-rose-800 dark:text-rose-300">Remove Corner</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">JSON Preview</div>
                <textarea readOnly value={exportJson} rows={18} className="w-full rounded-2xl border border-slate-300 px-3 py-2 font-mono text-xs dark:border-slate-700 dark:bg-slate-900" />
              </div>
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
}